import { useId, useState } from "react";
import { useMutation } from "convex/react";
import { ShieldAlert } from "lucide-react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { estSlugValide, slugDepuisTitre } from "@repo/ent-struct";
import { Button, Dialog, Input, Label } from "@repo/ui";
import { messageErreur, type LigneScorecard } from "@repo/scorecard-editor";

/**
 * Crée un modèle global à partir de la version publiée d'une scorecard
 * d'organisation. Le modèle ne garde aucune trace de l'organisation, mais son
 * contenu peut encore la citer : il naît en brouillon, à relire puis publier.
 */
export function CreerModeleDialog({
  source,
  onFermer,
  onCree,
}: {
  source: LigneScorecard;
  onFermer: () => void;
  onCree: (id: Id<"scorecards">) => void;
}) {
  const ids = { titre: useId(), slug: useId() };
  const creer = useMutation(api.scorecards.creerModeleDepuisScorecard);
  const [titre, setTitre] = useState(source.titre);
  const [slugSaisi, setSlugSaisi] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // Le slug suit le titre tant qu'on ne l'a pas touché.
  const slug = slugSaisi ?? slugDepuisTitre(titre);
  const pret = titre.trim() !== "" && estSlugValide(slug);

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      onCree(await creer({ scorecardId: source._id, titre, slug }));
    } catch (e) {
      setErreur(messageErreur(e));
      setEnCours(false);
    }
  };

  return (
    <Dialog isOpen onClose={onFermer} title="Créer un modèle" className="max-w-lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (pret) void valider();
        }}
      >
        <p className="text-sm text-muted-foreground">
          Le modèle reprend la <strong>version {source.numeroVersionPubliee} publiée</strong>{" "}
          de « {source.titre} ». Il ne garde aucun lien avec l'organisation, et
          la scorecard du client n'est pas modifiée.
        </p>
        <p className="flex items-start gap-2 rounded-surface border border-etat-alerte-trait bg-etat-alerte-surface p-3 text-sm text-etat-alerte-contenu">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          Il sera créé en brouillon : relisez-le pour retirer le nom du client et
          ce qui lui est propre, puis publiez-le pour pouvoir l'assigner.
        </p>
        <div>
          <Label htmlFor={ids.titre} className="mb-1">
            Titre du modèle
          </Label>
          <Input id={ids.titre} value={titre} onChange={(e) => setTitre(e.target.value)} />
        </div>
        <div>
          <Label htmlFor={ids.slug} className="mb-1">
            Slug (identifiant technique, définitif)
          </Label>
          <Input
            id={ids.slug}
            value={slug}
            aria-invalid={!estSlugValide(slug) || undefined}
            onChange={(e) => setSlugSaisi(e.target.value)}
          />
        </div>
        {erreur && (
          <p role="alert" className="text-sm text-destructive">
            {erreur}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onFermer}>
            Annuler
          </Button>
          <Button type="submit" disabled={!pret || enCours}>
            Créer le modèle
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
